import React, { useEffect, useMemo, useState } from "react";
import * as Icons from "react-feather";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "store/auth";

const isItemActive = (pathname, item) => {
  if (!item || !item.to) return false;
  if (pathname === item.to || pathname.startsWith(`${item.to}/`)) return true;
  return (item.childs || []).some(
    (child) => pathname === child.to || pathname.startsWith(`${child.to}/`)
  );
};

const NotificationBadge = ({ count }) =>
  Number(count) > 0 ? (
    <span className="mr-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 text-xs text-white">
      {count}
    </span>
  ) : null;

const IconLoader = ({ name }) => {
  const DynamicIcon = Icons[name] || Icons.Circle;
  return <DynamicIcon aria-hidden="true" />;
};

export default function SideBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const menus = useMemo(
    () => (auth.userMeta?.panel_menus || []).filter((item) => item.allow),
    [auth.userMeta]
  );
  const matchedMenu = menus.find((item) => isItemActive(location.pathname, item));
  const [expandedMenu, setExpandedMenu] = useState(matchedMenu?.to || "");

  useEffect(() => {
    if (matchedMenu?.to) setExpandedMenu(matchedMenu.to);
  }, [location.pathname, matchedMenu?.to]);

  const selectMenu = (item) => {
    const children = item.childs || [];
    if (!children.length) {
      navigate(item.to);
      return;
    }
    setExpandedMenu((current) => (current === item.to ? "" : item.to));
  };

  const fullName = [auth.userMeta?.first_name, auth.userMeta?.last_name]
    .filter(Boolean)
    .join(" ");

  return (
    <nav className="side-nav" aria-label="منوی مدیریت">
      <ul className="sticky top-4">
        <li className="flex flex-col items-center justify-center gap-y-3 text-white">
          <div className="image-fit zoom-in h-12 w-12 scale-110 overflow-hidden rounded-full shadow-lg">
            <img alt="تصویر حساب کاربری" src="/images/account.jpg" />
          </div>
          {fullName ? <span>{fullName}</span> : null}
        </li>
        <li aria-hidden="true" className="side-nav__devider my-3" />

        {menus.map((item) => {
          const children = (item.childs || []).filter((child) => child.allow);
          const active = isItemActive(location.pathname, item);
          const expanded = expandedMenu === item.to;

          return (
            <li key={item.to || item.title}>
              <button
                type="button"
                onClick={() => selectMenu(item)}
                className={`side-menu w-full text-right ${active ? "side-menu--active" : ""}`}
                aria-expanded={children.length ? expanded : undefined}
              >
                <span className="side-menu__icon">
                  <IconLoader name={item.icon} />
                </span>
                <span className="side-menu__title">
                  {item.title}
                  <NotificationBadge count={item.notification_count} />
                  {children.length ? (
                    <Icons.ChevronDown
                      aria-hidden="true"
                      className={`side-menu__sub-icon transition-transform ${expanded ? "rotate-180" : ""}`}
                    />
                  ) : null}
                </span>
              </button>

              {children.length ? (
                <ul className={expanded ? "side-menu__sub-open" : ""}>
                  {children.map((child) => (
                    <li key={child.to || child.title}>
                      <Link
                        to={child.to}
                        className={`side-menu ${isItemActive(location.pathname, child) ? "side-menu--active" : ""}`}
                      >
                        <span className="side-menu__icon">
                          <IconLoader name={child.icon} />
                        </span>
                        <span className="side-menu__title">
                          {child.title}
                          <NotificationBadge count={child.notification_count} />
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export { IconLoader, isItemActive };
